import { ImportedTargetDefinition } from './imported-target.model';

export class TargetDrawer {
  private static getAdaptiveScale(
    canvas: HTMLCanvasElement,
    shots: any[],
    baseScale: number
  ): number {
    const distances = shots
      .map(shot => Math.hypot(Number(shot?.XCoord) || 0, Number(shot?.YCoord) || 0) * 1000)
      .filter(distance => Number.isFinite(distance));
    const furthestShotMm = Math.max(...distances, 0);
    if (furthestShotMm <= 0) return baseScale;

    const desiredPixelsPerMm = Math.min(canvas.width, canvas.height) * 0.36 / furthestShotMm;
    const zoomedScale = desiredPixelsPerMm / 4;
    return Math.min(10, Math.max(baseScale, zoomedScale));
  }

  private static drawShotNumber(
    ctx: CanvasRenderingContext2D,
    value: string,
    shotX: number,
    shotY: number,
    fontSize: number,
    selected: boolean
  ): void {
    ctx.font = `${fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = selected ? '#111827' : '#ffffff';
    ctx.strokeStyle = selected ? '#ffffff' : '#111827';
    ctx.lineWidth = Math.max(1, fontSize * 0.12);
    ctx.strokeText(value, shotX, shotY);
    ctx.fillText(value, shotX, shotY);
  }

  // --- Helper: Draw rings ---
  private static drawRings(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    mmToPx: (mm: number) => number,
    ringDiameters: Record<number, number>,
    labelCount: number,
    isInner: (i: number) => boolean,
    labelOffset: number,
    fontSize: number
  ): void {
    const ringCount = Object.keys(ringDiameters).length;
    for (let i = 1; i <= ringCount; i++) {
      const radiusPx = mmToPx(ringDiameters[i] / 2);

      ctx.beginPath();
      ctx.arc(centerX, centerY, radiusPx, 0, Math.PI * 2);
      ctx.fillStyle = isInner(i) ? '#000' : '#fff';
      ctx.strokeStyle = isInner(i) ? '#fff' : '#000';
      ctx.fill();
      ctx.stroke();

      if (i <= labelCount) {
        ctx.fillStyle = isInner(i) ? '#fff' : '#000';
        ctx.font = `${fontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const label = i.toString();
        ctx.fillText(label, centerX, centerY - radiusPx + labelOffset);
        ctx.fillText(label, centerX, centerY + radiusPx - labelOffset);
        ctx.fillText(label, centerX - radiusPx + labelOffset, centerY);
        ctx.fillText(label, centerX + radiusPx - labelOffset, centerY);
      }
    }
  }

  // --- Helper: Animate shots ---
  private static animateShots(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    mmToPx: (mm: number) => number,
    shots: any[],
    shotRadius: number,
    fontSize: number,
    color: string = 'blue',
    startIndex: number, 
    endIndex: number,
    selectedIndex: number | null = null,
    useInterval: boolean = false,  
  ): void {
    let index = startIndex;

    if (useInterval) {
      const interval = setInterval(() => {
        if (index > endIndex || index >= shots.length) {
          clearInterval(interval);
          return;
        }

        // Draw shots up to the current index
        for (let i = startIndex; i <= index; i++) {
          const shot = shots[i];
          const shotX = centerX + mmToPx(shot.XCoord) * 1000;
          const shotY = centerY - mmToPx(shot.YCoord) * 1000;
          ctx.beginPath();
          ctx.arc(shotX, shotY, mmToPx(shotRadius), 0, Math.PI * 2);
          ctx.fillStyle = (i === index || i === selectedIndex) ? 'yellow' : color;
          ctx.fill();

          this.drawShotNumber(
            ctx,
            (shot.TargetShotZAP ?? shot.OrderOfShot ?? (i + 1)).toString(),
            shotX,
            shotY,
            fontSize,
            i === index || i === selectedIndex
          );
        }
        index++;
      }, 300);
    } else {
      // Draw all shots between startIndex and endIndex without interval
      for (let i = startIndex; i <= endIndex && i < shots.length; i++) {
        const shot = shots[i];
        const shotX = centerX + mmToPx(shot.XCoord) * 1000;
        const shotY = centerY - mmToPx(shot.YCoord) * 1000;
        ctx.beginPath();
        ctx.arc(shotX, shotY, mmToPx(shotRadius), 0, Math.PI * 2);
        ctx.fillStyle = i === selectedIndex ? 'yellow' : color;
        ctx.fill();

        this.drawShotNumber(
          ctx,
          (shot.TargetShotZAP ?? shot.OrderOfShot ?? (i + 1)).toString(),
          shotX,
          shotY,
          fontSize,
          i === selectedIndex
        );
      }
    }
  }

  /**
   * SIUS exercise XML files contain target metadata, not ring geometry. The
   * parser therefore resolves their AP10/AR10/CROAR target names to the
   * existing built-in profiles. Shot coordinates already use millimetres
   * from the target centre, with positive Y upwards; the existing drawer
   * converts Y to canvas coordinates by subtracting it from the centre.
   */
  static drawImportedXmlTarget(
    canvas: HTMLCanvasElement,
    importedTarget: ImportedTargetDefinition,
    shots: any[],
    startIndex: number = 0,
    endIndex: number = shots.length - 1,
    selectedIndex: number | null = null
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const profile = importedTarget.geometry.profile;
    const baseScale = profile === 'pistol' ? 1 : profile === 'air serial' ? 2.5 : 4;
    const scale = profile === 'air rifle'
      ? this.getAdaptiveScale(canvas, shots, baseScale)
      : baseScale;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const mmToPx = (mm: number): number => mm * scale * 4;
    const ringDiameters = importedTarget.geometry.rings.reduce<Record<number, number>>((rings, ring) => {
      rings[ring.value] = ring.diameterMm;
      return rings;
    }, {});

    this.drawRings(
      ctx,
      centerX,
      centerY,
      mmToPx,
      ringDiameters,
      profile === 'pistol' ? 9 : 8,
      index => profile === 'pistol' ? index >= 7 : profile === 'air rifle' ? index >= 4 : index >= 7,
      profile === 'pistol' ? 15 * scale : profile === 'air serial' ? 10 * scale : 5 * scale,
      profile === 'pistol' ? 11 * scale : 8 * scale
    );

    if (profile !== 'air serial') {
      ctx.beginPath();
      ctx.arc(centerX, centerY, mmToPx(profile === 'pistol' ? 2.5 : 0.25), 0, Math.PI * 2);
      ctx.strokeStyle = 'white';
      ctx.stroke();
    }

    this.animateShots(
      ctx,
      centerX,
      centerY,
      mmToPx,
      shots,
      profile === 'pistol' ? 2 : profile === 'air rifle' ? 2.25 : 1.5,
      9 * scale,
      'blue',
      startIndex,
      endIndex,
      selectedIndex
    );
  }

  // --- Pistol Target ---
  static drawPistolTarget(canvas: HTMLCanvasElement, shots: any[], scale: number = 1, startIndex: number = 0, endIndex: number = shots.length - 1, selectedIndex: number | null = null): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const mmToPx = (mm: number): number => mm * scale * 4;

    const ringDiameters: Record<number, number> = {
      1: 155.5, 2: 139.5, 3: 123.5, 4: 107.5, 5: 91.5,
      6: 75.5, 7: 59.5, 8: 43.5, 9: 27.5, 10: 11.5, 11: 5.0
    };

    this.drawRings(ctx, centerX, centerY, mmToPx, ringDiameters, 9, i => i >= 7, 15 * scale, 11 * scale);

    // Draw center dot
    ctx.beginPath();
    ctx.arc(centerX, centerY, mmToPx(2.5), 0, Math.PI * 2);
    ctx.strokeStyle = 'white';
    ctx.stroke();

    this.animateShots(ctx, centerX, centerY, mmToPx, shots, 2, 9 * scale, 'blue', startIndex, endIndex, selectedIndex);
  }

  // --- Serial Air Rifle Target ---
  static drawSerialAirRifleTarget(canvas: HTMLCanvasElement, shots: any[], scale: number = 1, startIndex: number = 0, endIndex: number = shots.length - 1, selectedIndex: number | null = null): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const mmToPx = (mm: number): number => mm * scale * 4;

    const ringDiameters: Record<number, number> = {
      1: 83.0, 2: 74.0, 3: 65.0, 4: 56.0, 5: 47.0,
      6: 38.0, 7: 29.0, 8: 20.0, 9: 11.0, 10: 2.0
    };

    this.drawRings(ctx, centerX, centerY, mmToPx, ringDiameters, 8, i => i >= 7, 10 * scale, 8 * scale);

    this.animateShots(ctx, centerX, centerY, mmToPx, shots, 1.5, 9 * scale, 'blue', startIndex, endIndex, selectedIndex);
  }

  // --- Full Air Rifle Target ---
  static drawFullAirRifleTarget(canvas: HTMLCanvasElement, shots: any[], scale: number = 4, startIndex: number = 0, endIndex: number = shots.length - 1, selectedIndex: number | null = null): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const adaptiveScale = this.getAdaptiveScale(canvas, shots, scale);
    const mmToPx = (mm: number): number => mm * adaptiveScale * 4;

    const ringDiameters: Record<number, number> = {
      1: 45.5, 2: 40.5, 3: 35.5, 4: 30.5, 5: 25.5,
      6: 20.5, 7: 15.5, 8: 10.5, 9: 5.5, 10: 0.5
    };

    this.drawRings(ctx, centerX, centerY, mmToPx, ringDiameters, 8, i => i >= 4, 5 * adaptiveScale, 8 * adaptiveScale);

    // Draw center dot
    ctx.beginPath();
    ctx.arc(centerX, centerY, mmToPx(0.25), 0, Math.PI * 2);
    ctx.strokeStyle = 'white';
    ctx.stroke();

    this.animateShots(ctx, centerX, centerY, mmToPx, shots, 2.25, 9 * adaptiveScale, 'blue', startIndex, endIndex, selectedIndex);
  }
}